import { useEffect, useId, useRef } from 'react';
import type { Etat } from '../partage/api/types';
import { PastilleEtat } from '../apparence';
import { C_EST_FAIT, J_ATTENDS_UN_RETOUR, MOTS_ETAT } from '../partage/vocabulaire';

/** Ce que l'annulation rendra (ticket 29), tel que la fiche le reçoit. */
export interface AnnulationDeLaFiche {
  etatAvant: Etat | null;
  avecInventaire: boolean;
  enCours: boolean;
}

/**
 * Les gestes de la fiche, communs à l'ordinateur (dans son pied) et au téléphone (en bas, sous
 * le pouce) : « C'est fait », « J'attends un retour », ou, pour une bulle acquise ou en attente,
 * son état et le geste qui le défait. Les mêmes boutons, les mêmes mots, un seul endroit à la
 * fois.
 *
 * Après « J'attends un retour », l'état et le geste qui le défait sont deux choses (retour 10
 * des testeurs, ticket 09 de retours-testeurs). L'état se lit seul, sans cadre ni fond, pour
 * ne pas avoir l'air d'un bouton ; le bouton est un verbe et dit ce qu'il rend. Annuler rend
 * l'état d'avant (ticket 29) ; sans trace du geste (page rechargée), le bouton dit « Remettre
 * à faire », ce qui est exactement ce que fera le clic.
 *
 * Rend un fragment : l'appelant les pose dans sa rangée.
 */
export function GestesDeLaFiche({
  bulleId,
  etat,
  annulation,
  onValider,
  onAttendre,
  onDefaire,
}: {
  bulleId: string;
  etat: Etat;
  annulation: AnnulationDeLaFiche;
  onValider: () => void;
  onAttendre: () => void;
  onDefaire: () => void;
}) {
  /* Les deux états qu'un geste du pied a posés, et qu'il peut donc défaire (ticket 29). */
  const defaisable = etat === 'fait' || etat === 'en_attente_confirmation';
  /* Le bouton « Annuler » est décrit par l'état qu'il défait : au clavier, on entend les deux. */
  const idEtat = useId();

  /*
   * « J'attends un retour » et « Annuler » se remplacent l'un l'autre, la fiche restant
   * ouverte : le bouton cliqué disparaît et le focus tomberait sur la page. On le pose sur
   * celui qui le remplace, seulement après un de ces deux clics : quand l'état a changé, et
   * pas tant que le bouton est désactivé (l'écriture en cours), où il refuserait le focus.
   */
  const boutonAttendre = useRef<HTMLButtonElement>(null);
  const boutonDefaire = useRef<HTMLButtonElement>(null);
  /** L'état au moment du clic, tant que le focus reste à rendre. */
  const rendreLeFocus = useRef<Etat | null>(null);
  useEffect(() => {
    if (rendreLeFocus.current === null || rendreLeFocus.current === etat) return;
    const cible = boutonDefaire.current ?? boutonAttendre.current;
    if (!cible || cible.disabled) return;
    rendreLeFocus.current = null;
    cible.focus();
  }, [etat, annulation.enCours]);
  // Une autre bulle, ou une écriture refusée puis reprise ailleurs : le focus n'a plus rien à rendre.
  useEffect(() => {
    rendreLeFocus.current = null;
  }, [bulleId]);

  return (
    <>
      {defaisable && (
        <span
          id={idEtat}
          className={`fiche-pied-etat fiche-pied-etat--${etat === 'fait' ? 'acquis' : 'monde'}`}
        >
          <span aria-hidden="true">
            <PastilleEtat etat={etat} taille={16} />
          </span>
          {MOTS_ETAT[etat]}
        </span>
      )}
      {etat !== 'fait' && (
        <button className="bouton" onClick={onValider}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
            <path d="M3 7.9l3.1 3.1L12.3 4.6" stroke="currentColor" strokeWidth="1.7" />
          </svg>
          {C_EST_FAIT}
        </button>
      )}
      {/* Une bulle acquise se défait d'abord : l'attente ne la décoche pas en passant. */}
      {etat === 'a_faire' || etat === 'sans_objet' ? (
        <button
          ref={boutonAttendre}
          className="bouton bouton-discret"
          onClick={() => {
            rendreLeFocus.current = etat;
            onAttendre();
          }}
        >
          {J_ATTENDS_UN_RETOUR}
        </button>
      ) : null}
      {defaisable && (
        <button
          ref={boutonDefaire}
          className="bouton bouton-discret"
          onClick={() => {
            rendreLeFocus.current = etat;
            onDefaire();
          }}
          disabled={annulation.enCours}
          aria-describedby={idEtat}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M4.6 3.2L2 5.8l2.6 2.6M2.3 5.8h6.2a3.3 3.3 0 010 6.6H6"
              stroke="currentColor"
              strokeWidth="1.4"
            />
          </svg>
          {annulation.etatAvant ? (
            <span>
              Annuler
              <span className="fiche-defaire-rend">
                , revenir à « {MOTS_ETAT[annulation.etatAvant]} »
              </span>
            </span>
          ) : (
            'Remettre à faire'
          )}
        </button>
      )}
    </>
  );
}

/** Sous les gestes, après « C'est fait » sur une bulle qui contenait encore des choses. */
export function MotDuPied({ etat, annulation }: { etat: Etat; annulation: AnnulationDeLaFiche }) {
  if (etat !== 'fait' || !annulation.avecInventaire) return null;
  return (
    <p className="fiche-pied-mot">
      Annuler ne rend que son état : ce que tu as coché, gardé comme idée ou déplacé dedans
      reste comme tu l’as laissé.
    </p>
  );
}
